import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/PrintOutlined";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";
import BranchFilterSelect from "components/BranchFilterSelect";

import useMinistryData from "layouts/tables/data/ministryTableData";

function Ministry() {
  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedMinistry,
    setSelectedMinistry,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddMinistry,
    branches,
    canFilterByBranch,
    branchFilter,
    setBranchFilter,
  } = useMinistryData();

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading ministries...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Church Ministries"
        subtitle="Create and manage the ministries members can belong to."
        actions={
          <>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddMinistry}>
              New Ministry
            </Button>
            <IconButton onClick={handlePrint} sx={{ border: 1, borderColor: "divider" }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </>
        }
      />

      {canFilterByBranch && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <BranchFilterSelect branches={branches} value={branchFilter} onChange={setBranchFilter} />
        </Box>
      )}

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted
          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
          showTotalEntries
          canSearch
          searchLabel="Search ministries..."
        />
      </Card>

      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={selectedMinistry?.id ? "Edit Ministry" : "Add Ministry"}
        onSave={handleEditSubmit}
        saveLabel={selectedMinistry?.id ? "Save Changes" : "Create Ministry"}
        maxWidth="xs"
      >
        <FormSection>
          <TextField
            label="Ministry Name"
            value={selectedMinistry?.ministryName || ""}
            onChange={(e) =>
              setSelectedMinistry({ ...selectedMinistry, ministryName: e.target.value })
            }
            fullWidth
            margin="normal"
            required
          />
          <TextField
            select
            label="Status"
            value={selectedMinistry?.status || "active"}
            onChange={(e) => setSelectedMinistry({ ...selectedMinistry, status: e.target.value })}
            fullWidth
            margin="normal"
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          {canFilterByBranch && (
            <TextField
              select
              label="Branch"
              value={selectedMinistry?.branchId || ""}
              onChange={(e) =>
                setSelectedMinistry({ ...selectedMinistry, branchId: e.target.value || null })
              }
              fullWidth
              margin="normal"
              helperText="Leave blank for a church-wide ministry"
            >
              <MenuItem value="">
                <em>Church-wide</em>
              </MenuItem>
              {branches.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </FormSection>
      </FormDialog>

      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedMinistry?.ministryName}" ministry?`}
        confirmLabel="Delete"
      />
    </Box>
  );
}

export default Ministry;
